import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Confirm,
  Dropdown,
  Form,
  Grid,
  Icon,
  Input,
  Label,
  Loader,
  Message,
  Modal,
  TextArea,
} from 'semantic-ui-react';
import { API, copy, showError, showInfo, showSuccess } from '../../helpers';
import { authHeader } from '../../helpers/auth-header';
import { StudioHero, StudioPage } from '../../components/StudioPage';
import './index.css';

const MODE_CHANNEL = 'channel';
const MODE_GROUP = 'group';
const MODE_CUSTOM = 'custom';
const EMPTY_GROUP_VALUE = '__empty_group__';
const TAB_CHAT = 'chat';
const TAB_IMAGE = 'image';
// 模型 → 支持尺寸映射，与后端 relay/billing/ratio/image.go 保持同步
const IMAGE_SIZE_CONFIG = {
  'dall-e-2': ['256x256', '512x512', '1024x1024'],
  'dall-e-3': ['1024x1024', '1024x1792', '1792x1024'],
  'ali-stable-diffusion-xl': ['512x1024', '1024x768', '1024x1024', '576x1024', '1024x576'],
  'ali-stable-diffusion-v1.5': ['512x1024', '1024x768', '1024x1024', '576x1024', '1024x576'],
  'wanx-v1': ['1024x1024', '720x1280', '1280x720'],
  'step-1x-medium': ['256x256', '512x512', '768x768', '1024x1024', '1280x800', '800x1280'],
};
// 未匹配到已知模型时的默认尺寸（含 4K 等高清分辨率）
const DEFAULT_IMAGE_SIZES = [
  '1024x1024', '1024x1792', '1792x1024',
  '512x512', '768x768', '1280x800', '800x1280',
  '1920x1080', '2560x1440', '2880x1800',
  '3840x2160', '4096x2160',
];
const IMAGE_QUALITIES = ['standard', 'hd'];
const API_BASE = process.env.REACT_APP_SERVER
  ? process.env.REACT_APP_SERVER
  : '';
const TEMPORARY_CHANNELS_API = '/api/playground/temporary-channels';
const BOTTOM_THRESHOLD = 96;
let messageSequence = 0;

function getTargetLabel(item) {
  if (!item) return '';
  if (item.mode === MODE_GROUP)
    return `${item.display_name || item.name} (${item.channel_count || 0})`;
  return `${item.display_name || item.name || '未命名'} #${item.id}`;
}

function getTemporaryStatus(channel, t) {
  const status = channel?.status;
  if (status === 1 || status === 'active' || status === 'enabled') {
    return {
      color: 'green',
      icon: 'check circle',
      text: t('playground.temporary.status_active'),
    };
  }
  if (
    status === 2 ||
    status === 3 ||
    status === 4 ||
    status === 'disabled' ||
    status === 'inactive'
  ) {
    return {
      color: 'red',
      icon: 'ban',
      text: t('playground.temporary.status_disabled'),
    };
  }
  return {
    color: 'grey',
    icon: 'question circle',
    text: status || t('playground.temporary.status_unknown'),
  };
}

function getTemporaryFailure(channel) {
  return (
    channel?.temporary_last_error ||
    channel?.last_failure_reason ||
    channel?.last_error ||
    channel?.failure_message ||
    channel?.disabled_reason ||
    channel?.probe_error ||
    ''
  );
}

function getTemporaryFailureCount(channel) {
  return (
    channel?.temporary_failure_count ??
    channel?.failure_count ??
    channel?.consecutive_failures ??
    channel?.probe_failure_count ??
    0
  );
}

function normalizeImageSource(image) {
  if (image?.url) {
    try {
      const parsed = new URL(image.url, window.location.origin);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return parsed.href;
      }
    } catch {
      return '';
    }
  }
  if (!image?.b64_json) return '';
  if (image.b64_json.startsWith('data:')) {
    return /^data:image\/[a-zA-Z0-9.+-]+;base64,/i.test(image.b64_json)
      ? image.b64_json
      : '';
  }
  return /^[a-zA-Z0-9+/=\r\n]+$/.test(image.b64_json)
    ? `data:image/png;base64,${image.b64_json}`
    : '';
}

function createMessage(role, content, meta) {
  messageSequence += 1;
  return {
    id: `${Date.now()}-${messageSequence}`,
    role,
    content,
    images: [],
    ...(meta ? { meta } : {}),
  };
}

function parseSSEBlock(block) {
  return block
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim())
    .join('\n');
}

function extractDelta(payload) {
  if (!payload || payload === '[DONE]') return { done: payload === '[DONE]' };
  try {
    const json = JSON.parse(payload);
    if (json.type === 'playground_meta' || json.type === 'playground_error') {
      return { meta: json };
    }
    const choice = json.choices && json.choices[0];
    const content = choice?.delta?.content ?? choice?.text ?? '';
    return { text: typeof content === 'string' ? content : '' };
  } catch {
    return { text: '' };
  }
}

const TypingIndicator = () => (
  <span className='playground-typing' aria-label='generating'>
    <span />
    <span />
    <span />
  </span>
);

const StatusPill = ({ icon, children, tone = '' }) => (
  <span className={`playground-meta-pill ${tone ? `is-${tone}` : ''}`}>
    {icon && <Icon name={icon} />}
    {children}
  </span>
);

const ChatMessage = ({
  item,
  isActive,
  isImageMode,
  onCopy,
  onDownload,
  onImageLoad,
  t,
}) => {
  const isUser = item.role === 'user';
  const meta = item.meta || {};
  const images = item.images || [];
  const isGenerating =
    !isUser && meta.status === 'running' && !item.content && images.length === 0;

  return (
    <div
      className={`playground-message is-${item.role} ${
        images.length > 0 ? 'is-image-result' : ''
      }`}
    >
      <div className='playground-message-avatar' aria-hidden='true'>
        <Icon name={isUser ? 'user outline' : 'magic'} />
      </div>
      <div className='playground-message-main'>
        <div className='playground-message-heading'>
          <span>
            {isUser
              ? t('playground.chat.you')
              : isImageMode
              ? t('playground.image.result')
              : t('playground.chat.assistant')}
          </span>
          {!isUser && meta.status === 'running' && (
            <span className='playground-inline-status'>
              <span className='playground-status-dot' />
              {t('playground.status.generating')}
            </span>
          )}
        </div>
        <div className='playground-bubble'>
          {isGenerating ? (
            <div className='playground-generating-row'>
              <TypingIndicator />
              <span>{t('playground.generating')}</span>
            </div>
          ) : (
            <>
              {item.content && <span>{item.content}</span>}
              {images.length > 0 && (
                <div
                  className={`playground-image-gallery has-${Math.min(
                    images.length,
                    4
                  )}-images`}
                >
                  {images.map((img, idx) => {
                    const src = normalizeImageSource(img);
                    if (!src) return null;
                    return (
                      <figure
                        key={idx}
                        className='playground-image-card'
                      >
                        <a href={src} target='_blank' rel='noreferrer'>
                          <img
                            src={src}
                            alt={
                              img.revised_prompt ||
                              t('playground.image.generated_alt', {
                                prompt: item.imagePrompt || '',
                              })
                            }
                            onLoad={onImageLoad}
                          />
                        </a>
                        {img.revised_prompt && (
                          <figcaption>{img.revised_prompt}</figcaption>
                        )}
                        <button
                          type='button'
                          className='playground-image-download'
                          onClick={() => onDownload && onDownload(src, idx)}
                          title={t('playground.image.download')}
                          aria-label={t('playground.image.download')}
                        >
                          <Icon name='download' />
                        </button>
                      </figure>
                    );
                  })}
                </div>
              )}
            </>
          )}
          {isActive && item.content && (
            <span className='playground-stream-cursor' />
          )}
        </div>
        {!isUser && (item.content || images.length > 0 || meta.status !== 'running') && (
          <div className='playground-message-footer'>
            <div className='playground-message-meta'>
              {meta.channel_name && (
                <StatusPill icon='sitemap'>{meta.channel_name}</StatusPill>
              )}
              {meta.actual_model && (
                <StatusPill icon='cube'>{meta.actual_model}</StatusPill>
              )}
              {meta.elapsed_ms !== undefined && (
                <StatusPill icon='clock outline'>
                  {(meta.elapsed_ms / 1000).toFixed(2)}s
                </StatusPill>
              )}
              {meta.status === 'completed' && (
                <StatusPill icon='check' tone='success'>
                  {t('playground.status.completed')}
                </StatusPill>
              )}
              {meta.status === 'stopped' && (
                <StatusPill icon='stop' tone='warning'>
                  {t('playground.status.stopped')}
                </StatusPill>
              )}
              {meta.status === 'failed' && (
                <StatusPill icon='warning sign' tone='danger'>
                  {meta.error || t('playground.status.failed')}
                </StatusPill>
              )}
            </div>
            {item.content && (
              <button
                type='button'
                className='playground-copy-button'
                onClick={() => onCopy(item.content)}
                title={t('playground.buttons.copy')}
                aria-label={t('playground.buttons.copy')}
              >
                <Icon name='copy outline' />
                <span>{t('playground.buttons.copy')}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Playground = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(MODE_CHANNEL);
  const [targets, setTargets] = useState({ channels: [], groups: [] });
  const [targetValue, setTargetValue] = useState('');
  const [baseURL, setBaseURL] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [models, setModels] = useState([]);
  const [model, setModel] = useState('');
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [importing, setImporting] = useState(false);
  const [temporaryChannels, setTemporaryChannels] = useState([]);
  const [temporaryModalOpen, setTemporaryModalOpen] = useState(false);
  const [temporaryLoading, setTemporaryLoading] = useState(false);
  const [temporarySaving, setTemporarySaving] = useState(false);
  const [temporaryAction, setTemporaryAction] = useState('');
  const [temporaryProbeModels, setTemporaryProbeModels] = useState({});
  const [temporaryDeleteTarget, setTemporaryDeleteTarget] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [imageMessages, setImageMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const abortRef = useRef(null);
  const activeAssistantIdRef = useRef(null);
  const activeStartedAtRef = useRef(null);
  const bufferRef = useRef('');
  const typeTimerRef = useRef(null);
  const messagesViewportRef = useRef(null);
  const isNearBottomRef = useRef(true);

  // 生图模式状态
  const [tab, setTab] = useState(TAB_CHAT);
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageSize, setImageSize] = useState(DEFAULT_IMAGE_SIZES[0]);
  const [imageN, setImageN] = useState(1);
  const [imageQuality, setImageQuality] = useState(IMAGE_QUALITIES[0]);
  const [imageGenerating, setImageGenerating] = useState(false);
  const imageAbortRef = useRef(null);

  // 根据所选模型动态计算可用尺寸列表
  const imageSizeOptions = useMemo(() => {
    const sizes = IMAGE_SIZE_CONFIG[model] || DEFAULT_IMAGE_SIZES;
    return sizes.map((s) => ({ key: s, text: s, value: s }));
  }, [model]);

  // 切换模型时，若当前尺寸不在新模型的支持列表中，自动重置为第一个
  useEffect(() => {
    const available = IMAGE_SIZE_CONFIG[model] || DEFAULT_IMAGE_SIZES;
    if (!available.includes(imageSize)) {
      setImageSize(available[0]);
    }
  }, [model]);
  const currentMessages = tab === TAB_IMAGE ? imageMessages : chatMessages;
  const busy = streaming || imageGenerating;

  const currentTargets =
    mode === MODE_GROUP ? targets.groups : targets.channels;
  const selectedTarget = useMemo(
    () =>
      currentTargets.find(
        (item) =>
          String(item.mode === MODE_GROUP ? groupValue(item.name) : item.id) ===
          String(targetValue)
      ),
    [currentTargets, targetValue]
  );
  const targetOptions = currentTargets.map((item) => ({
    key: item.mode === MODE_GROUP ? groupValue(item.name) : item.id,
    value: item.mode === MODE_GROUP ? groupValue(item.name) : String(item.id),
    text: getTargetLabel(item),
    description: item.models?.length ? `${item.models.length} models` : '',
  }));
  const modelOptions = normalizeModels(
    models.length ? models : selectedTarget?.models || []
  ).map((item) => ({ key: item, text: item, value: item }));

  const targetSummary = useMemo(() => {
    if (mode === MODE_CUSTOM) {
      return baseURL.trim() || t('playground.context.custom_upstream');
    }
    return selectedTarget
      ? getTargetLabel(selectedTarget)
      : t('playground.context.no_target');
  }, [baseURL, mode, selectedTarget, t]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const viewport = messagesViewportRef.current;
    if (!viewport) return;
    viewport.scrollTo({ top: viewport.scrollHeight, behavior });
    isNearBottomRef.current = true;
    setShowScrollButton(false);
  }, []);

  const handleMessagesScroll = () => {
    const viewport = messagesViewportRef.current;
    if (!viewport) return;
    const distance =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    const isNearBottom = distance <= BOTTOM_THRESHOLD;
    isNearBottomRef.current = isNearBottom;
    setShowScrollButton(!isNearBottom);
  };

  const handleImageLoad = () => {
    if (isNearBottomRef.current) {
      scrollToBottom('auto');
    }
  };

  const switchTab = (nextTab) => {
    if (busy || nextTab === tab) return;
    setTab(nextTab);
    isNearBottomRef.current = true;
    setShowScrollButton(false);
    requestAnimationFrame(() => scrollToBottom('auto'));
  };

  const loadTargets = useCallback(async () => {
    setLoadingTargets(true);
    try {
      const res = await API.get('/api/playground/targets');
      const { success, message, data } = res.data;
      if (success) {
        const nextTargets = data || { channels: [], groups: [] };
        setTargets(nextTargets);
        return nextTargets;
      }
      showError(message);
    } catch (error) {
      showError(error.message || t('playground.messages.targets_failed'));
    } finally {
      setLoadingTargets(false);
    }
    return null;
  }, [t]);

  const loadTemporaryChannels = useCallback(async () => {
    setTemporaryLoading(true);
    try {
      const res = await API.get(TEMPORARY_CHANNELS_API);
      const { success, message, data } = res.data;
      if (success) {
        const list = Array.isArray(data)
          ? data
          : data?.channels || data?.items || [];
        setTemporaryChannels(list);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(
        error.message || t('playground.messages.temporary_load_failed')
      );
    } finally {
      setTemporaryLoading(false);
    }
  }, [t]);

  const openTemporaryManager = () => {
    setTemporaryModalOpen(true);
    loadTemporaryChannels();
  };

  useEffect(() => {
    loadTargets();
    return () => {
      if (typeTimerRef.current) clearInterval(typeTimerRef.current);
      abortRef.current?.abort();
      imageAbortRef.current?.abort();
    };
  }, [loadTargets]);

  useEffect(() => {
    if (selectedTarget?.models?.length) {
      setModels(selectedTarget.models);
      setModel((current) =>
        selectedTarget.models.includes(current)
          ? current
          : selectedTarget.models[0] || ''
      );
    } else {
      setModels([]);
    }
  }, [selectedTarget]);

  useEffect(() => {
    if (isNearBottomRef.current) {
      scrollToBottom(streaming ? 'auto' : 'smooth');
    } else if (currentMessages.length) {
      setShowScrollButton(true);
    }
  }, [currentMessages, scrollToBottom, streaming]);

  const buildTargetPayload = () => {
    const payload = { mode };
    if (mode === MODE_CHANNEL) payload.channel_id = Number(targetValue);
    if (mode === MODE_GROUP)
      payload.group_name = targetValue === EMPTY_GROUP_VALUE ? '' : targetValue;
    if (mode === MODE_CUSTOM) {
      payload.base_url = baseURL.trim();
      payload.key = apiKey.trim();
    }
    return payload;
  };

  const fetchModels = async () => {
    if (mode !== MODE_CUSTOM && !targetValue) {
      showInfo(t('playground.messages.select_target'));
      return;
    }
    if (mode === MODE_CUSTOM && (!baseURL.trim() || !apiKey.trim())) {
      showInfo(t('playground.messages.base_key_required'));
      return;
    }
    setFetchingModels(true);
    try {
      const res = await API.post(
        '/api/playground/models',
        buildTargetPayload()
      );
      const { success, message, data } = res.data;
      if (success) {
        const nextModels = normalizeModels(data?.models || []);
        setModels(nextModels);
        if (nextModels[0]) setModel(nextModels[0]);
        showSuccess(
          t('playground.messages.models_loaded', { count: nextModels.length })
        );
        if (mode !== MODE_CUSTOM) loadTargets();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '拉取模型失败');
    } finally {
      setFetchingModels(false);
    }
  };

  const updateMessage = (setter, messageId, updater) => {
    if (!messageId) return;
    setter((previous) =>
      previous.map((item) => {
        if (item.id !== messageId) return item;
        return typeof updater === 'function'
          ? updater(item)
          : { ...item, ...updater };
      })
    );
  };

  const updateAssistant = (assistantId, updater) => {
    updateMessage(setChatMessages, assistantId, updater);
  };

  const updateImageAssistant = (assistantId, updater) => {
    updateMessage(setImageMessages, assistantId, updater);
  };

  const clearTypewriter = (
    flush = true,
    assistantId = activeAssistantIdRef.current
  ) => {
    if (typeTimerRef.current) {
      clearInterval(typeTimerRef.current);
      typeTimerRef.current = null;
    }
    const pendingText = bufferRef.current;
    bufferRef.current = '';
    if (flush && pendingText && assistantId) {
      updateAssistant(assistantId, (item) => ({
        ...item,
        content: `${item.content || ''}${pendingText}`,
      }));
    }
  };

  const appendAssistantText = (assistantId, text) => {
    bufferRef.current += text;
    if (typeTimerRef.current) return;
    typeTimerRef.current = setInterval(() => {
      const next = bufferRef.current.slice(0, 2);
      bufferRef.current = bufferRef.current.slice(2);
      if (!next) {
        clearInterval(typeTimerRef.current);
        typeTimerRef.current = null;
        return;
      }
      updateAssistant(assistantId, (item) => ({
        ...item,
        content: `${item.content || ''}${next}`,
      }));
    }, 18);
  };

  const updateAssistantMeta = (assistantId, patch) => {
    updateAssistant(assistantId, (item) => ({
      ...item,
      meta: { ...(item.meta || {}), ...patch },
    }));
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || busy) return;
    if (!model.trim()) {
      showInfo(t('playground.messages.model_required'));
      return;
    }
    if (mode !== MODE_CUSTOM && !targetValue) {
      showInfo(t('playground.messages.select_target'));
      return;
    }
    if (mode === MODE_CUSTOM && (!baseURL.trim() || !apiKey.trim())) {
      showInfo(t('playground.messages.base_key_required'));
      return;
    }

    clearTypewriter(false);
    const startedAt = Date.now();
    const userMessage = createMessage('user', text);
    const assistantMessage = createMessage('assistant', '', {
      status: 'running',
      started_at: startedAt,
    });
    const nextMessages = [...chatMessages, userMessage, assistantMessage];
    activeAssistantIdRef.current = assistantMessage.id;
    activeStartedAtRef.current = startedAt;
    isNearBottomRef.current = true;
    setChatMessages(nextMessages);
    setInput('');
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    bufferRef.current = '';
    const requestPayload = {
      ...buildTargetPayload(),
      model: model.trim(),
      messages: nextMessages
        .filter((item) => item.role !== 'assistant' || item.content)
        .map((item) => ({ role: item.role, content: item.content })),
    };

    try {
      const response = await fetch(`${API_BASE}/api/playground/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify(requestPayload),
        signal: controller.signal,
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('text/event-stream')) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.message || '聊天请求失败');
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let pending = '';
      let done = false;
      let requestFailed = false;
      while (!done) {
        const chunk = await reader.read();
        done = chunk.done;
        pending += decoder.decode(chunk.value || new Uint8Array(), {
          stream: !done,
        });
        const parts = pending.split(/\r?\n\r?\n/);
        pending = parts.pop() || '';
        for (const part of parts) {
          const payloadText = parseSSEBlock(part);
          const parsed = extractDelta(payloadText);
          if (parsed.done) {
            done = true;
            break;
          }
          if (parsed.meta) {
            if (parsed.meta.type === 'playground_error') {
              requestFailed = true;
              updateAssistantMeta(assistantMessage.id, {
                status: 'failed',
                error: parsed.meta.message,
                elapsed_ms: parsed.meta.elapsed_ms,
              });
            } else {
              updateAssistantMeta(assistantMessage.id, parsed.meta);
            }
          }
          if (parsed.text)
            appendAssistantText(assistantMessage.id, parsed.text);
        }
      }
      clearTypewriter(true, assistantMessage.id);
      if (!requestFailed) {
        updateAssistantMeta(assistantMessage.id, {
          status: 'completed',
          elapsed_ms: Date.now() - startedAt,
        });
      }
    } catch (error) {
      clearTypewriter(true, assistantMessage.id);
      if (error.name !== 'AbortError') {
        updateAssistantMeta(assistantMessage.id, {
          status: 'failed',
          error: error.message,
          elapsed_ms: Date.now() - startedAt,
        });
        showError(error.message || '聊天失败');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      activeAssistantIdRef.current = null;
      activeStartedAtRef.current = null;
    }
  };

  const stopStreaming = () => {
    const assistantId = activeAssistantIdRef.current;
    const startedAt = activeStartedAtRef.current;
    abortRef.current?.abort();
    clearTypewriter(true, assistantId);
    updateAssistantMeta(assistantId, {
      status: 'stopped',
      elapsed_ms: startedAt ? Date.now() - startedAt : 0,
    });
    setStreaming(false);
  };

  const importChannel = async () => {
    if (!baseURL.trim() || !apiKey.trim()) {
      showInfo(t('playground.messages.base_key_required'));
      return;
    }
    setImporting(true);
    try {
      const res = await API.post('/api/playground/import', {
        base_url: baseURL.trim(),
        key: apiKey.trim(),
        models,
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess(message || t('playground.messages.import_success'));
        await loadTargets();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '导入渠道失败');
    } finally {
      setImporting(false);
    }
  };

  const saveTemporaryChannel = async () => {
    if (!baseURL.trim() || !apiKey.trim()) {
      showInfo(t('playground.messages.base_key_required'));
      return;
    }
    setTemporarySaving(true);
    try {
      const res = await API.post(TEMPORARY_CHANNELS_API, {
        base_url: baseURL.trim(),
        key: apiKey.trim(),
        models: normalizeModels(models),
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess(message || t('playground.messages.temporary_save_success'));
        await Promise.all([loadTemporaryChannels(), loadTargets()]);
        setTemporaryModalOpen(true);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(
        error.message || t('playground.messages.temporary_save_failed')
      );
    } finally {
      setTemporarySaving(false);
    }
  };

  const activateTemporaryChannel = async (channel) => {
    const channelModels = normalizeModels(channel.models || []);
    setBaseURL(channel.base_url || '');
    setApiKey('');
    setModels(channelModels);
    setModel((current) =>
      channelModels.includes(current) ? current : channelModels[0] || ''
    );

    const nextTargets = await loadTargets();
    const savedChannelId = channel.channel_id ?? channel.id;
    const target = nextTargets?.channels?.find(
      (item) =>
        String(item.id) === String(savedChannelId) ||
        String(item.id) === String(channel.id) ||
        String(item.channel_id) === String(savedChannelId)
    );
    if (target) {
      setMode(MODE_CHANNEL);
      setTargetValue(String(target.id));
      setTemporaryModalOpen(false);
      showSuccess(t('playground.messages.temporary_selected'));
      return;
    }

    setMode(MODE_CUSTOM);
    setTargetValue('');
    showInfo(t('playground.messages.temporary_key_not_returned'));
  };

  const fetchTemporaryModels = async (channel) => {
    const actionKey = `models:${channel.id}`;
    setTemporaryAction(actionKey);
    try {
      const res = await API.post(
        `${TEMPORARY_CHANNELS_API}/${channel.id}/models`
      );
      const { success, message, data } = res.data;
      if (success) {
        const nextModels = normalizeModels(
          Array.isArray(data) ? data : data?.models || []
        );
        if (nextModels.length) {
          setTemporaryChannels((previous) =>
            previous.map((item) =>
              item.id === channel.id
                ? {
                    ...item,
                    ...(!Array.isArray(data) && data ? data : {}),
                    models: nextModels,
                  }
                : item
            )
          );
        }
        showSuccess(
          message ||
            t('playground.messages.models_loaded', {
              count: nextModels.length,
            })
        );
        await loadTemporaryChannels();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(
        error.message || t('playground.messages.temporary_models_failed')
      );
    } finally {
      setTemporaryAction('');
    }
  };

  const probeTemporaryChannel = async (channel) => {
    const channelModels = normalizeModels(channel.models || []);
    const probeModel =
      temporaryProbeModels[channel.id] || channelModels[0] || '';
    if (!probeModel) {
      showInfo(t('playground.messages.temporary_probe_model_required'));
      return;
    }

    const actionKey = `probe:${channel.id}`;
    setTemporaryAction(actionKey);
    try {
      const res = await API.post(
        `${TEMPORARY_CHANNELS_API}/${channel.id}/probe`,
        { model: probeModel }
      );
      const { success, message } = res.data;
      if (success) {
        showSuccess(
          message || t('playground.messages.temporary_probe_success')
        );
        await loadTemporaryChannels();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(
        error.message || t('playground.messages.temporary_probe_failed')
      );
    } finally {
      setTemporaryAction('');
    }
  };

  const reactivateTemporaryChannel = async (channel) => {
    const actionKey = `reactivate:${channel.id}`;
    setTemporaryAction(actionKey);
    try {
      const res = await API.put(
        `${TEMPORARY_CHANNELS_API}/${channel.id}/reactivate`
      );
      const { success, message } = res.data;
      if (success) {
        showSuccess(message || t('playground.messages.temporary_reactivated'));
        await Promise.all([loadTemporaryChannels(), loadTargets()]);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(
        error.message || t('playground.messages.temporary_reactivate_failed')
      );
    } finally {
      setTemporaryAction('');
    }
  };

  const deleteTemporaryChannel = async () => {
    if (!temporaryDeleteTarget) return;
    const actionKey = `delete:${temporaryDeleteTarget.id}`;
    setTemporaryAction(actionKey);
    try {
      const res = await API.delete(
        `${TEMPORARY_CHANNELS_API}/${temporaryDeleteTarget.id}`
      );
      const { success, message } = res.data;
      if (success) {
        showSuccess(message || t('playground.messages.temporary_deleted'));
        setTemporaryChannels((previous) =>
          previous.filter((item) => item.id !== temporaryDeleteTarget.id)
        );
        setTemporaryDeleteTarget(null);
        await loadTargets();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(
        error.message || t('playground.messages.temporary_delete_failed')
      );
    } finally {
      setTemporaryAction('');
    }
  };

  const clearMessages = () => {
    if (tab === TAB_IMAGE) {
      imageAbortRef.current?.abort();
      setImageMessages([]);
    } else {
      if (streaming) stopStreaming();
      clearTypewriter(false);
      setChatMessages([]);
    }
    setShowScrollButton(false);
    isNearBottomRef.current = true;
  };

  const copyMessage = async (content) => {
    const copied = await copy(content);
    if (copied) {
      showSuccess(t('playground.messages.copied'));
    } else {
      showError(t('playground.messages.copy_failed'));
    }
  };

  const downloadImage = async (src, idx) => {
    const isRemote = /^https?:\/\//i.test(src);
    const fallbackWindow = isRemote ? window.open('', '_blank') : null;
    if (fallbackWindow) fallbackWindow.opener = null;
    try {
      const response = await fetch(src);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const mimeExtension = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/webp': 'webp',
        'image/gif': 'gif',
      }[blob.type];
      const extension = mimeExtension || 'png';
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `playground-image-${Date.now()}-${idx + 1}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      fallbackWindow?.close();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      if (isRemote && fallbackWindow) {
        fallbackWindow.opener = null;
        fallbackWindow.location.replace(src);
        showInfo(t('playground.image.download_fallback'));
        return;
      }
      showError(error.message || t('playground.image.download_failed'));
    }
  };

  const generateImage = async () => {
    const prompt = imagePrompt.trim();
    if (!prompt || busy) return;
    if (!model.trim()) {
      showInfo(t('playground.messages.model_required'));
      return;
    }
    if (mode !== MODE_CUSTOM && !targetValue) {
      showInfo(t('playground.messages.select_target'));
      return;
    }
    if (mode === MODE_CUSTOM && (!baseURL.trim() || !apiKey.trim())) {
      showInfo(t('playground.messages.base_key_required'));
      return;
    }

    const startedAt = Date.now();
    const userMessage = createMessage('user', prompt);
    const assistantMessage = createMessage('assistant', '', {
      status: 'running',
      started_at: startedAt,
    });
    const nextMessages = [...imageMessages, userMessage, assistantMessage];
    isNearBottomRef.current = true;
    setImageMessages(nextMessages);
    setImagePrompt('');
    setImageGenerating(true);
    scrollToBottom();

    const controller = new AbortController();
    imageAbortRef.current = controller;
    const requestPayload = {
      ...buildTargetPayload(),
      model: model.trim(),
      prompt,
      n: imageN,
      size: imageSize,
      quality: imageQuality,
      response_format: 'url',
    };

    try {
      const response = await fetch(`${API_BASE}/api/playground/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify(requestPayload),
        signal: controller.signal,
      });
      const json = await response.json().catch(() => null);
      if (!json || !json.success) {
        throw new Error(json?.message || t('playground.image.request_failed'));
      }
      const imageItems = Array.isArray(json.data?.data)
        ? json.data.data.filter((item) => item?.url || item?.b64_json)
        : [];
      if (!imageItems.length) {
        throw new Error(t('playground.image.empty_result'));
      }
      updateImageAssistant(assistantMessage.id, (item) => ({
        ...item,
        images: imageItems,
        imagePrompt: prompt,
        meta: {
          ...(item.meta || {}),
          status: 'completed',
          channel_id: json.channel_id,
          channel_name: json.channel_name,
          model: json.model,
          actual_model: json.actual_model,
          elapsed_ms: json.elapsed_ms ?? Date.now() - startedAt,
        },
      }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        updateImageAssistant(assistantMessage.id, (item) => ({
          ...item,
          meta: {
            ...(item.meta || {}),
            status: 'failed',
            error: error.message,
            elapsed_ms: Date.now() - startedAt,
          },
        }));
        showError(error.message || t('playground.image.generate_failed'));
      } else {
        updateImageAssistant(assistantMessage.id, (item) => ({
          ...item,
          meta: {
            ...(item.meta || {}),
            status: 'stopped',
            elapsed_ms: Date.now() - startedAt,
          },
        }));
      }
    } finally {
      setImageGenerating(false);
      imageAbortRef.current = null;
    }
  };

  const stopImageGeneration = () => {
    imageAbortRef.current?.abort();
  };

  return (
    <StudioPage className='studio-playground-page'>
      <StudioHero
        icon='lab'
        kicker='AI STUDIO / PLAYGROUND'
        title={t('playground.title')}
        description={t('playground.subtitle')}
        className='playground-hero'
      >
        <Button
          icon='refresh'
          content={t('playground.buttons.refresh_targets')}
          loading={loadingTargets}
          onClick={loadTargets}
          className='playground-refresh-button'
        />
      </StudioHero>

      <div className='playground-layout'>
        <aside className='playground-config-panel'>
          <div className='playground-panel-heading'>
            <span className='playground-panel-step'>01</span>
            <div>
              <strong>{t('playground.config.title')}</strong>
              <span>{t('playground.config.subtitle')}</span>
            </div>
          </div>
          <Form>
            <Form.Field>
              <label>{t('playground.fields.mode')}</label>
              <div className='playground-mode-selector'>
                {[
                  {
                    value: MODE_CHANNEL,
                    label: t('playground.modes.channel'),
                    icon: 'sitemap',
                  },
                  {
                    value: MODE_GROUP,
                    label: t('playground.modes.group'),
                    icon: 'object group',
                  },
                  {
                    value: MODE_CUSTOM,
                    label: t('playground.modes.custom'),
                    icon: 'terminal',
                  },
                ].map((item) => {
                  const active = mode === item.value;
                  return (
                    <Button
                      key={item.value}
                      type='button'
                      active={active}
                      icon={item.icon}
                      content={item.label}
                      onClick={() => {
                        setMode(item.value);
                        setTargetValue('');
                      }}
                      className={`playground-mode-button ${
                        active ? 'is-active' : ''
                      }`}
                    />
                  );
                })}
              </div>
            </Form.Field>

            {mode !== MODE_CUSTOM ? (
              <Form.Field>
                <label>{t('playground.fields.target')}</label>
                <Dropdown
                  fluid
                  selection
                  search
                  loading={loadingTargets}
                  options={targetOptions}
                  value={targetValue}
                  onChange={(e, { value }) => setTargetValue(value)}
                  placeholder={t('playground.placeholders.target')}
                />
              </Form.Field>
            ) : (
              <>
                <Form.Field>
                  <label>{t('playground.fields.base_url')}</label>
                  <Input
                    value={baseURL}
                    onChange={(e) => setBaseURL(e.target.value)}
                    placeholder='https://api.example.com'
                  />
                </Form.Field>
                <Form.Field>
                  <label>{t('playground.fields.key')}</label>
                  <Input
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder='sk-...'
                    type='password'
                    autoComplete='new-password'
                  />
                </Form.Field>
              </>
            )}

            <Form.Field>
              <label>{t('playground.fields.model')}</label>
              <Dropdown
                fluid
                selection
                search
                allowAdditions
                options={modelOptions}
                value={model}
                onAddItem={(e, { value }) =>
                  setModels((previous) => normalizeModels([...previous, value]))
                }
                onChange={(e, { value }) => setModel(value)}
                placeholder={t('playground.placeholders.model')}
              />
            </Form.Field>

            <Grid
              columns={mode === MODE_CUSTOM ? 2 : 1}
              stackable
              className='playground-config-actions'
            >
              <Grid.Column>
                <Button
                  fluid
                  icon='download'
                  content={t('playground.buttons.fetch_models')}
                  loading={fetchingModels}
                  onClick={fetchModels}
                  className='playground-primary-button'
                />
              </Grid.Column>
              {mode === MODE_CUSTOM && (
                <Grid.Column>
                  <Button
                    fluid
                    icon='plus'
                    content={t('playground.buttons.import_channel')}
                    loading={importing}
                    onClick={importChannel}
                    className='playground-secondary-button'
                  />
                </Grid.Column>
              )}
            </Grid>

            {mode === MODE_CUSTOM && (
              <div className='playground-temporary-toolbar'>
                <Button
                  type='button'
                  icon='clock outline'
                  content={t('playground.buttons.save_temporary')}
                  loading={temporarySaving}
                  onClick={saveTemporaryChannel}
                  className='playground-primary-button'
                />
                <Button
                  type='button'
                  icon='settings'
                  content={t('playground.buttons.manage_temporary')}
                  onClick={openTemporaryManager}
                  className='playground-secondary-button'
                />
              </div>
            )}

            <Message size='small' className='playground-config-tip'>
              <Icon name='info circle' />
              {mode === MODE_CUSTOM
                ? t('playground.tips.custom')
                : t('playground.tips.saved')}
            </Message>
          </Form>
        </aside>

        <section className='playground-chat-panel'>
          <div
            className='playground-tab-bar'
            role='group'
            aria-label={t('playground.tabs.label')}
          >
            {[
              {
                value: TAB_CHAT,
                label: t('playground.tabs.chat'),
                icon: 'comments outline',
              },
              {
                value: TAB_IMAGE,
                label: t('playground.tabs.image'),
                icon: 'image outline',
              },
            ].map((item) => {
              const active = tab === item.value;
              return (
                <Button
                  key={item.value}
                  type='button'
                  icon={item.icon}
                  content={item.label}
                  active={active}
                  aria-pressed={active}
                  disabled={busy}
                  onClick={() => switchTab(item.value)}
                  className={`playground-tab-button ${
                    active ? 'is-active' : ''
                  }`}
                />
              );
            })}
          </div>
          <header className='playground-chat-header'>
            <div className='playground-chat-identity'>
              <span className='playground-ai-orb'>
                <Icon name={tab === TAB_IMAGE ? 'image outline' : 'magic'} />
              </span>
              <div>
                <strong>
                  {tab === TAB_IMAGE
                    ? t('playground.image.title')
                    : t('playground.chat.title')}
                </strong>
                <div className='playground-context-row'>
                  <span title={model || t('playground.context.no_model')}>
                    <Icon name='cube' />
                    {model || t('playground.context.no_model')}
                  </span>
                  <span title={targetSummary}>
                    <Icon name='sitemap' />
                    {targetSummary}
                  </span>
                </div>
              </div>
            </div>
            <div className='playground-chat-header-actions'>
              <span
                className={`playground-connection-state ${
                  busy ? 'is-streaming' : ''
                }`}
              >
                <span className='playground-status-dot' />
                {busy
                  ? t('playground.status.generating')
                  : t('playground.status.ready')}
              </span>
              <Button
                type='button'
                icon='trash alternate outline'
                onClick={clearMessages}
                disabled={!currentMessages.length}
                className='playground-clear-button'
                title={t('playground.buttons.clear')}
                aria-label={t('playground.buttons.clear')}
              />
            </div>
          </header>

          <div className='playground-chat-stage'>
            <div
              className='playground-messages'
              ref={messagesViewportRef}
              onScroll={handleMessagesScroll}
            >
              {currentMessages.length === 0 && (
                <div className='playground-empty-state'>
                  <span className='playground-empty-orb'>
                    <Icon
                      name={tab === TAB_IMAGE ? 'image outline' : 'comments outline'}
                    />
                  </span>
                  <h2>
                    {tab === TAB_IMAGE
                      ? t('playground.image.empty_title')
                      : t('playground.empty_title')}
                  </h2>
                  <p>
                    {tab === TAB_IMAGE
                      ? t('playground.image.empty')
                      : t('playground.empty')}
                  </p>
                  <div className='playground-empty-hint'>
                    <Icon name='bolt' />
                    {tab === TAB_IMAGE
                      ? t('playground.image.empty_hint')
                      : t('playground.empty_hint')}
                  </div>
                </div>
              )}
              {currentMessages.map((item) => (
                <ChatMessage
                  key={item.id}
                  item={item}
                  isActive={
                    streaming && item.id === activeAssistantIdRef.current
                  }
                  isImageMode={tab === TAB_IMAGE}
                  onCopy={copyMessage}
                  onDownload={downloadImage}
                  onImageLoad={handleImageLoad}
                  t={t}
                />
              ))}
            </div>
            {showScrollButton && (
              <button
                type='button'
                className='playground-scroll-bottom'
                onClick={() => scrollToBottom()}
              >
                <Icon name='arrow down' />
                {t('playground.buttons.scroll_bottom')}
              </button>
            )}
          </div>

          {tab === TAB_CHAT ? (
            <Form className='playground-composer' onSubmit={sendMessage}>
              <TextArea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('playground.placeholders.message')}
                rows={4}
                disabled={busy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <div className='playground-composer-actions'>
                <div className='playground-composer-context'>
                  <span>
                    <Icon name='keyboard outline' />
                    {t('playground.chat.shortcut')}
                  </span>
                  {model && (
                    <span className='playground-composer-model'>
                      <Icon name='cube' />
                      {model}
                    </span>
                  )}
                </div>
                <div className='playground-submit-wrap'>
                  {streaming ? (
                    <Button
                      type='button'
                      onClick={stopStreaming}
                      className='playground-stop-button'
                    >
                      <span className='playground-stop-icon' />
                      {t('playground.buttons.stop')}
                    </Button>
                  ) : (
                    <Button
                      type='submit'
                      disabled={!input.trim() || !model.trim()}
                      className='playground-send-button'
                    >
                      <span>{t('playground.buttons.send')}</span>
                      <Icon name='arrow up' />
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          ) : (
            <Form
              className='playground-composer playground-image-composer'
              onSubmit={(e) => {
                e.preventDefault();
                generateImage();
              }}
            >
              <TextArea
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                placeholder={t('playground.image.prompt_placeholder')}
                rows={3}
                disabled={busy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    generateImage();
                  }
                }}
              />
              <div className='playground-image-options'>
                <Form.Group inline widths='equal'>
                  <Form.Field>
                    <label>{t('playground.image.size')}</label>
                    <Dropdown
                      selection
                      search
                      allowAdditions
                      options={imageSizeOptions}
                      value={imageSize}
                      disabled={busy}
                      onAddItem={(e, { value }) => {
                        setImageSize(value);
                      }}
                      onChange={(e, { value }) => setImageSize(value)}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>{t('playground.image.quality')}</label>
                    <Dropdown
                      selection
                      options={IMAGE_QUALITIES.map((q) => ({
                        key: q,
                        text: t(`playground.image.quality_${q}`),
                        value: q,
                      }))}
                      value={imageQuality}
                      disabled={busy}
                      onChange={(e, { value }) => setImageQuality(value)}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>{t('playground.image.count')}</label>
                    <Dropdown
                      selection
                      options={[1, 2, 3, 4].map((n) => ({
                        key: n,
                        text: String(n),
                        value: n,
                      }))}
                      value={imageN}
                      disabled={busy}
                      onChange={(e, { value }) => setImageN(value)}
                    />
                  </Form.Field>
                </Form.Group>
              </div>
              <div className='playground-composer-actions'>
                <div className='playground-composer-context'>
                  <span>
                    <Icon name='keyboard outline' />
                    {t('playground.image.shortcut')}
                  </span>
                  {model && (
                    <span className='playground-composer-model'>
                      <Icon name='cube' />
                      {model}
                    </span>
                  )}
                </div>
                <div className='playground-submit-wrap'>
                  {imageGenerating ? (
                    <Button
                      type='button'
                      onClick={stopImageGeneration}
                      className='playground-stop-button'
                    >
                      <span className='playground-stop-icon' />
                      {t('playground.buttons.stop')}
                    </Button>
                  ) : (
                    <Button
                      type='submit'
                      disabled={!imagePrompt.trim() || !model.trim()}
                      className='playground-send-button'
                    >
                      <span>{t('playground.image.generate')}</span>
                      <Icon name='image' />
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          )}
        </section>
      </div>

      <Modal
        open={temporaryModalOpen}
        onClose={() => setTemporaryModalOpen(false)}
        size='large'
        className='playground-temporary-modal'
        closeIcon
      >
        <Modal.Header>
          <div className='playground-temporary-modal-heading'>
            <div>
              <strong>{t('playground.temporary.title')}</strong>
              <span>{t('playground.temporary.subtitle')}</span>
            </div>
            <Button
              type='button'
              size='mini'
              icon='refresh'
              content={t('playground.buttons.refresh_temporary')}
              loading={temporaryLoading}
              onClick={loadTemporaryChannels}
              className='playground-secondary-button'
            />
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          <Message size='small' info className='playground-temporary-key-note'>
            <Icon name='lock' />
            {t('playground.temporary.key_note')}
          </Message>

          {temporaryLoading && temporaryChannels.length === 0 ? (
            <div className='playground-temporary-empty'>
              <Loader active inline='centered' />
            </div>
          ) : temporaryChannels.length === 0 ? (
            <div className='playground-temporary-empty'>
              <Icon name='clock outline' />
              <strong>{t('playground.temporary.empty_title')}</strong>
              <span>{t('playground.temporary.empty')}</span>
            </div>
          ) : (
            <div className='playground-temporary-list'>
              {temporaryChannels.map((channel) => {
                const channelModels = normalizeModels(channel.models || []);
                const status = getTemporaryStatus(channel, t);
                const failure = getTemporaryFailure(channel);
                const failureCount = getTemporaryFailureCount(channel);
                const active = isTemporaryChannelActive(channel);
                const probeModel =
                  temporaryProbeModels[channel.id] || channelModels[0] || '';
                const probeOptions = channelModels.map((item) => ({
                  key: item,
                  text: item,
                  value: item,
                }));
                return (
                  <article
                    key={channel.id}
                    className='playground-temporary-card'
                  >
                    <div className='playground-temporary-card-head'>
                      <div className='playground-temporary-identity'>
                        <span className='playground-temporary-id'>
                          #{channel.id}
                        </span>
                        <div>
                          <strong>
                            {channel.name ||
                              t('playground.temporary.unnamed', {
                                id: channel.id,
                              })}
                          </strong>
                          <span title={channel.base_url}>
                            {channel.base_url || '-'}
                          </span>
                        </div>
                      </div>
                      <Label basic size='mini' color={status.color}>
                        <Icon name={status.icon} />
                        {status.text}
                      </Label>
                    </div>

                    <div className='playground-temporary-meta'>
                      <span>
                        <Icon name='cube' />
                        {t('playground.temporary.model_count', {
                          count: channelModels.length,
                        })}
                      </span>
                      {failureCount > 0 && (
                        <span className='is-danger'>
                          <Icon name='warning sign' />
                          {t('playground.temporary.failure_count', {
                            count: failureCount,
                          })}
                        </span>
                      )}
                    </div>

                    {channelModels.length > 0 && (
                      <div className='playground-temporary-models'>
                        {channelModels.slice(0, 4).map((item) => (
                          <span key={item} title={item}>
                            {item}
                          </span>
                        ))}
                        {channelModels.length > 4 && (
                          <span>+{channelModels.length - 4}</span>
                        )}
                      </div>
                    )}

                    {failure && (
                      <Message
                        size='tiny'
                        negative
                        className='playground-temporary-failure'
                      >
                        <Icon name='warning sign' />
                        <span>{failure}</span>
                      </Message>
                    )}

                    <div className='playground-temporary-card-actions'>
                      <Button
                        type='button'
                        size='mini'
                        icon='play'
                        content={t('playground.buttons.use_temporary')}
                        onClick={() => activateTemporaryChannel(channel)}
                        disabled={Boolean(temporaryAction)}
                        className='playground-primary-button'
                      />
                      <Button
                        type='button'
                        size='mini'
                        icon='download'
                        content={t('playground.buttons.fetch_models')}
                        loading={temporaryAction === `models:${channel.id}`}
                        disabled={Boolean(temporaryAction)}
                        onClick={() => fetchTemporaryModels(channel)}
                        className='playground-secondary-button'
                      />
                      <div className='playground-temporary-probe'>
                        <Dropdown
                          compact
                          selection
                          search
                          allowAdditions
                          options={probeOptions}
                          value={probeModel}
                          placeholder={t('playground.placeholders.probe_model')}
                          onAddItem={(event, { value }) =>
                            setTemporaryProbeModels((previous) => ({
                              ...previous,
                              [channel.id]: value,
                            }))
                          }
                          onChange={(event, { value }) =>
                            setTemporaryProbeModels((previous) => ({
                              ...previous,
                              [channel.id]: value,
                            }))
                          }
                        />
                        <Button
                          type='button'
                          size='mini'
                          icon='stethoscope'
                          content={t('playground.buttons.probe_temporary')}
                          loading={temporaryAction === `probe:${channel.id}`}
                          disabled={Boolean(temporaryAction)}
                          onClick={() => probeTemporaryChannel(channel)}
                          className='playground-secondary-button'
                        />
                      </div>
                      <Button
                        type='button'
                        size='mini'
                        basic
                        color='green'
                        icon='redo'
                        content={t('playground.buttons.reactivate_temporary')}
                        loading={temporaryAction === `reactivate:${channel.id}`}
                        disabled={active || Boolean(temporaryAction)}
                        onClick={() => reactivateTemporaryChannel(channel)}
                      />
                      <Button
                        type='button'
                        size='mini'
                        basic
                        negative
                        icon='trash'
                        content={t('playground.buttons.delete_temporary')}
                        loading={temporaryAction === `delete:${channel.id}`}
                        disabled={Boolean(temporaryAction)}
                        onClick={() => setTemporaryDeleteTarget(channel)}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </Modal.Content>
      </Modal>

      <Confirm
        open={Boolean(temporaryDeleteTarget)}
        content={t('playground.temporary.delete_confirm', {
          name:
            temporaryDeleteTarget?.name ||
            `#${temporaryDeleteTarget?.id || ''}`,
        })}
        cancelButton={t('playground.buttons.cancel')}
        confirmButton={t('playground.buttons.confirm_delete_temporary')}
        onCancel={() => setTemporaryDeleteTarget(null)}
        onConfirm={deleteTemporaryChannel}
      />
    </StudioPage>
  );
};

function normalizeModels(models) {
  const values = Array.isArray(models)
    ? models
    : String(models || '').split(/[\n,]/);
  return Array.from(
    new Set(values.map((item) => String(item || '').trim()).filter(Boolean))
  ).sort();
}

function isTemporaryChannelActive(channel) {
  return (
    channel?.status === 1 ||
    channel?.status === 'active' ||
    channel?.status === 'enabled'
  );
}

function groupValue(name) {
  return String(name || '').trim() === '' ? EMPTY_GROUP_VALUE : name;
}

export default Playground;
